import React, { useState } from "react";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { useTranslation } from "@me8eon/translation";
import {
    SelectedCategory,
    SentimentRow,
    fmt,
    positivePct,
    sentimentColor,
} from "../poData";
import ChartTooltip, { TooltipState } from "../components/ChartTooltip";
import TabSkeleton from "../components/TabSkeleton";
import TabError from "../components/TabError";
import { DashboardRange } from "../../../services/dashboardApi";
import { useDashboardData } from "../useDashboardData";

type Props = {
    range: DashboardRange;
    periodDays: number;
    isCustomPeriod: boolean;
    categoryFilter: SelectedCategory[];
    companyFilter: string[];
};

const UserSentimentTab: React.FC<Props> = ({
    range,
    periodDays,
    isCustomPeriod,
    categoryFilter,
    companyFilter,
}) => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.userSentiment.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    const { kpis, sentiments, loading, error } = useDashboardData(
        range,
        categoryFilter,
        companyFilter
    );
    const chartMax = Math.max(
        1,
        ...sentiments.map((c) => c.likes + c.dislikes)
    );
    const [expanded, setExpanded] = useState<string | null>(null);
    const [tip, setTip] = useState<TooltipState | null>(null);
    const [searchValue, setSearchValue] = useState("");

    const toggle = (cat: string) =>
        setExpanded((prev) => (prev === cat ? null : cat));

    const searchTerm = searchValue.trim().toLowerCase();
    const matchesSearch = (s: string) => s.toLowerCase().includes(searchTerm);

    const showTip = (e: React.MouseEvent<HTMLDivElement>, c: SentimentRow) => {
        const host = e.currentTarget.closest(".po-hbar") as HTMLElement | null;
        if (!host) return;
        const rect = host.getBoundingClientRect();
        setTip({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            containerWidth: rect.width,
            title: c.category,
            rows: [
                { label: tp("likes"), value: c.likes, tone: "good" },
                { label: tp("dislikes"), value: c.dislikes, tone: "bad" },
            ],
        });
    };

    if (loading) {
        return (
            <div className="po-sentiment">
                <TabSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="po-sentiment">
                <TabError />
            </div>
        );
    }

    return (
        <div className="po-sentiment">
            <div className="po-section-head">
                <div>
                    <h2>{tp("title")}</h2>
                    <p className="po-section-head__sub">
                        {fmt(kpis.likes)} {tp("summaryLikes")} ·{" "}
                        {fmt(kpis.dislikes)} {tp("summaryDislikes")} ·{" "}
                        {tc(isCustomPeriod ? "for" : "last")} {periodDays}{" "}
                        {tc(periodDays === 1 ? "day" : "days")}
                    </p>
                </div>
                <div className="po-section-head__actions">
                    <div className="po-chart-legend">
                        <span>
                            <i className="swatch swatch--good" /> {tp("likes")}
                        </span>
                        <span>
                            <i className="swatch swatch--bad" />{" "}
                            {tp("dislikes")}
                        </span>
                    </div>
                    <div className="po-search">
                        <EonUiIcon
                            name="search"
                            size="small"
                            className="po-search__icon"
                        />
                        <input
                            type="search"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={tp("searchPlaceholder")}
                        />
                    </div>
                </div>
            </div>

            {/* Category feedback detail (chart + list merged) */}
            <div className="po-card po-feedback-card">
                <h3>{tp("detailTitle")}</h3>
                <div className="po-feedback">
                    {sentiments
                        .map((c, i) => {
                            // Search only narrows the drill-down lists below; row stats always reflect the full data.
                            const matchedNegatives = searchTerm
                                ? c.negatives.filter(matchesSearch)
                                : c.negatives;
                            const matchedPositives = searchTerm
                                ? c.positives.filter(matchesSearch)
                                : c.positives;
                            const matchCount =
                                matchedNegatives.length + matchedPositives.length;
                            return { c, i, matchedNegatives, matchedPositives, matchCount };
                        })
                        // While searching, show the best-matching categories first; counts/bars/pct stay
                        // untouched — only the row order changes. No search term = original order, unchanged.
                        .sort((a, b) => (searchTerm ? b.matchCount - a.matchCount : 0))
                        .map(({ c, i, matchedNegatives, matchedPositives, matchCount }) => {
                        const total = c.likes + c.dislikes;
                        const widthPct = (total / chartMax) * 100;
                        const likeShare = (c.likes / total) * 100;
                        const pct = positivePct(c.likes, c.dislikes);
                        const isOpen = expanded === c.category;
                        return (
                            <div
                                className="po-feedback__row"
                                key={`${c.category}-${i}`}
                            >
                                <button
                                    className="po-feedback__head"
                                    onClick={() => toggle(c.category)}
                                >
                                    <span className="po-feedback__chevron">
                                        <EonUiIcon
                                            size="small"
                                            name={
                                                isOpen
                                                    ? "chevron_small_down"
                                                    : "chevron_small_right"
                                            }
                                        />
                                    </span>
                                    <span className="po-feedback__name">
                                        {c.category}{" "}
                                        {searchTerm && matchCount > 0 && (
                                            <span className="po-badge po-badge--match">
                                                {matchCount}{" "}
                                                {tp(matchCount === 1 ? "matchSuffix" : "matchesSuffix")}
                                            </span>
                                        )}
                                    </span>
                                    <div className="po-feedback__track_wrapper">
                                        <span className="po-feedback__dislikes">
                                            <EonUiIcon
                                                size="small"
                                                name="thumb_up"
                                                className="po-thumb po-thumb--down"
                                            />
                                            {fmt(c.dislikes)}
                                        </span>
                                        <div
                                            className="po-hbar__track po-feedback__track"
                                            onMouseMove={(e) => showTip(e, c)}
                                            onMouseLeave={() => setTip(null)}
                                        >
                                            <div
                                                className="po-hbar__stack"
                                                style={{
                                                    width: `${widthPct}%`,
                                                }}
                                            >
                                                <div
                                                    className="po-hbar__fill po-hbar__fill--bad"
                                                    style={{
                                                        width: `${100 - likeShare}%`,
                                                    }}
                                                />
                                                <div
                                                    className="po-hbar__fill po-hbar__fill--good"
                                                    style={{
                                                        width: `${likeShare}%`,
                                                    }}
                                                />
                                            </div>
                                            {tip &&
                                                tip.title === c.category && (
                                                    <ChartTooltip tip={tip} />
                                                )}
                                        </div>
                                        <span className="po-feedback__likes">
                                            <EonUiIcon
                                                size="small"
                                                name="thumb_up"
                                                className="po-thumb po-thumb--up"
                                            />
                                            {fmt(c.likes)}
                                        </span>
                                    </div>
                                    <span className="po-feedback__pct">
                                        <span className={sentimentColor(pct)}>
                                            {pct}%
                                        </span>{" "}
                                        <span className="po-feedback__pct-label">
                                            {tp("positive")}
                                        </span>
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="po-feedback__body">
                                        <div className="po-feedback__col">
                                            <div className="po-feedback__col-title">
                                                <EonUiIcon
                                                    size="small"
                                                    name="thumb_up"
                                                    className="po-thumb po-thumb--down"
                                                />{" "}
                                                {tp("thingsAttention")}
                                            </div>
                                            <ul className="po-feedback__list">
                                                {matchedNegatives.map(
                                                    (n, i) => (
                                                        <li key={`n${i}`}>
                                                            <a
                                                                className="po-feedback__list-link"
                                                                href={`/ai?q=${encodeURIComponent(n)}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={tp(
                                                                    "askAiHint"
                                                                )}
                                                            >
                                                                {n}
                                                            </a>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                        <div className="po-feedback__col">
                                            <div className="po-feedback__col-title">
                                                <EonUiIcon
                                                    size="small"
                                                    name="thumb_up"
                                                    className="po-thumb po-thumb--up"
                                                />{" "}
                                                {tp("thingsWell")}
                                            </div>
                                            <ul className="po-feedback__list">
                                                {matchedPositives.map(
                                                    (p, i) => (
                                                        <li key={`p${i}`}>
                                                            {p}
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default UserSentimentTab;
